import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Product type from producer_products table, formatted for UI display.
 * Images are resolved via media_assets (single source of truth).
 */
export interface ProducerProduct {
  id: string;
  title: string;
  slug: string | null;
  description: string | null;
  price: number; // In kronor (converted from öre)
  currencyCode: string;
  imageUrl: string | null;
  imageAlt: string | null;
  availableForSale: boolean;
  status: string;
  productType: 'simple' | 'bundle';
  createdAt: string;
  pickupInstructions: string | null;
  stockLimit: number | null;
}

export interface UseProducerShopProductsOptions {
  limit?: number;
  onlyActive?: boolean;
}

export interface UseProducerShopProductsResult {
  products: ProducerProduct[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  refetch: () => void;
}

/**
 * Validate UUID format
 */
function isValidUUID(str: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}

/**
 * Hook for fetching products from producer_products table.
 * Always filters by producer_id - never shows products from other producers.
 * Product images are NOT resolved here — use useProductCoverImages or
 * useProductPreviewImage for image resolution via media_assets.
 */
export function useProducerShopProducts(
  producerId: string | undefined,
  options: UseProducerShopProductsOptions = {}
): UseProducerShopProductsResult {
  const { limit, onlyActive = true } = options;

  const [products, setProducts] = useState<ProducerProduct[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    // Validate producerId - if invalid, return empty result
    if (!producerId || !isValidUUID(producerId)) {
      setProducts([]);
      setTotalCount(0);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchProducts() {
      setLoading(true);
      setError(null);

      try {
        // Build query - always filter by producer_id
        let query = supabase
          .from('producer_products')
          .select('*', { count: 'exact' })
          .eq('producer_id', producerId)
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: false });

        // Filter by status if onlyActive - show public, sold_out, upcoming (hide draft/archived)
        if (onlyActive) {
          query = query.in('status', ['public', 'sold_out', 'upcoming']);
        }

        // Apply limit if specified
        if (limit && limit > 0) {
          query = query.limit(limit);
        }

        const { data, error: queryError, count } = await query;

        if (cancelled) return;

        if (queryError) {
          console.error('Error fetching producer products:', queryError);
          setError('Kunde inte hämta produkter.');
          setProducts([]);
          setTotalCount(0);
          return;
        }

        // Transform to UI format — imageUrl is null; resolved via media_assets elsewhere
        const transformedProducts: ProducerProduct[] = (data || []).map((p) => ({
          id: p.id,
          title: p.title,
          slug: (p as any).slug ?? null,
          description: p.description,
          price: p.price_ore / 100, // Convert öre to kronor
          currencyCode: p.currency || 'SEK',
          imageUrl: null, // Resolved via media_assets (useProductCoverImages)
          imageAlt: p.title,
          availableForSale: p.status === 'public',
          status: p.status,
          productType: (p.product_type as 'simple' | 'bundle') || 'simple',
          createdAt: p.created_at,
          pickupInstructions: p.pickup_instructions ?? null,
          stockLimit: p.stock_limit ?? null,
        }));

        setProducts(transformedProducts);
        setTotalCount(count ?? transformedProducts.length);
      } catch (err) {
        if (!cancelled) {
          console.error('Exception fetching producer products:', err);
          setError('Ett oväntat fel uppstod.');
          setProducts([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchProducts();

    return () => {
      cancelled = true;
    };
  }, [producerId, limit, onlyActive, refetchTrigger]);

  const refetch = () => setRefetchTrigger((prev) => prev + 1);

  return { products, loading, error, totalCount, refetch };
}
